/**
 * Presentation flow API (M06-3) — page sync + annotations + presenter claim.
 *
 * BE endpoints (app/api/v1/endpoints/courtrooms.py):
 *   POST   /courtrooms/{id}/presentation/presenter/claim
 *   POST   /courtrooms/{id}/presentation/presenter/release
 *   PATCH  /courtrooms/{id}/presentation/page          {page}
 *   POST   /courtrooms/{id}/presentation/annotations   {page,x,y,w,h,color?}
 *   DELETE /courtrooms/{id}/presentation/annotations?page=
 */
import { apiSlice } from './apiSlice'
import type { Courtroom } from './courtroomsApi'

export interface PresentationAnnotation {
  id: string
  page: number
  x: number // fraction 0..1 (relative left của page render)
  y: number
  w: number
  h: number
  color?: string
  by_user_id?: string
  by_role?: string
  created_at?: string
}

export const presentationApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    claimPresenter: builder.mutation<Courtroom, string>({
      query: (courtroomId) => ({
        url: `/courtrooms/${courtroomId}/presentation/presenter/claim`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, id) => [{ type: 'Courtroom', id }],
    }),
    releasePresenter: builder.mutation<Courtroom, string>({
      query: (courtroomId) => ({
        url: `/courtrooms/${courtroomId}/presentation/presenter/release`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, id) => [{ type: 'Courtroom', id }],
    }),
    setPresentationPage: builder.mutation<
      Courtroom,
      { courtroomId: string; page: number }
    >({
      query: ({ courtroomId, page }) => ({
        url: `/courtrooms/${courtroomId}/presentation/page`,
        method: 'PATCH',
        body: { page },
      }),
      invalidatesTags: (_r, _e, { courtroomId }) => [{ type: 'Courtroom', id: courtroomId }],
    }),
    addAnnotation: builder.mutation<
      Courtroom,
      {
        courtroomId: string
        page: number
        x: number
        y: number
        w: number
        h: number
        color?: string
      }
    >({
      query: ({ courtroomId, ...body }) => ({
        url: `/courtrooms/${courtroomId}/presentation/annotations`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { courtroomId }) => [{ type: 'Courtroom', id: courtroomId }],
    }),
    clearAnnotations: builder.mutation<
      Courtroom,
      { courtroomId: string; page?: number }
    >({
      query: ({ courtroomId, page }) => ({
        url: `/courtrooms/${courtroomId}/presentation/annotations`,
        method: 'DELETE',
        params: page !== undefined ? { page } : undefined,
      }),
      invalidatesTags: (_r, _e, { courtroomId }) => [{ type: 'Courtroom', id: courtroomId }],
    }),
  }),
})

export const {
  useClaimPresenterMutation,
  useReleasePresenterMutation,
  useSetPresentationPageMutation,
  useAddAnnotationMutation,
  useClearAnnotationsMutation,
} = presentationApi
